/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  Card,
  Container,
  Label,
  TextInput,
  Typography,
  useAlertContext,
} from '@bw/alloy-react';
import { useNavigate } from 'react-router';
import ExpressHeader from 'components/ExpressHeader';
import { useSendTNVerificationCode } from 'hooks/express/useSendTNVerificationCode';
import { useSendTNVerification } from 'hooks/express/useSendTNVerification';
import { useSearchParams } from 'react-router-dom';

export interface LoginValues {
  username: string;
  password: string;
  rememberme: boolean;
}

const TNVerification: React.FC = () => {
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('token');
  const tn = searchParams.get('phoneNumber');
  const email = searchParams.get('email');
  const sendTNVerificationCode = useSendTNVerificationCode();
  const sendTNVerification = useSendTNVerification();
  const [values, setValues] = useState(Array(6).fill(''));
  const { alertDanger, alertSuccess } = useAlertContext();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tn || !registrationId) {
      navigate('/express');
      return;
    }

    const fetchData = async () => {
      try {
        await sendTNVerification({
          input: { phoneNumber: tn, email },
        });
      } catch (e) {
        alertDanger(
          e?.data || 'We were unable to message the provided phone number.',
        );
      }
    };

    fetchData();
  }, [
    tn,
    registrationId,
    email,
    navigate,
    alertSuccess,
    alertDanger,
    sendTNVerification,
  ]);

  useEffect(() => {
    alertSuccess('Thanks for verifying your email address');
  }, [alertSuccess]);

  const handleChange = (value, index) => {
    const val = value.slice(-1);
    const newValues = [...values];
    newValues[index] = val;
    setValues(newValues);

    if (val && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Backspace → move to previous input if empty
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const verify = async () => {
    const code = values.join('');
    try {
      setLoading(true);
      await sendTNVerificationCode({
        input: {
          email,
          phoneNumber: tn,
          code,
        },
      });

      navigate(`/new-password?token=${registrationId}`, {
        state: { registrationId },
      });
    } catch (e) {
      setLoading(false);
      alertDanger({
        message: (
          <>
            That code doesn&apos;t match.{' '}
            <Button textOnly onClick={sendNewCode}>
              Try a new one
            </Button>
          </>
        ),
      });
    }
  };

  const sendNewCode = async () => {
    try {
      setLoading(true);
      await sendTNVerification({
        input: { phoneNumber: tn, email },
      });
      alertSuccess(`We sent a confirmation code to ${tn}`);
    } catch (e) {
      alertDanger(e?.data || 'Resend failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const allFilled = values.every((v) => v !== '');

  return (
    <Container
      breakpoint="md"
      className="d-flex flex-column flex-grow gap-5 flex-grow-1"
    >
      <ExpressHeader />
      <Card className="p-5">
        <div className="d-flex flex-column gap-2">
          <Typography variant="h2">Verify your phone number</Typography>
          <Typography tag="div" variant="help-text">
            Check your texts for a verification code. Didn&apos;t get it?{' '}
            <Button textOnly onClick={sendNewCode} data-bw="send-new-code">
              Send a new code
            </Button>
          </Typography>
        </div>
        <div>
          <Label required>Code sent to {tn}</Label>
          <section className="d-flex gap-3">
            {values.map((val, i) => (
              <TextInput
                key={i}
                type="text"
                inputMode="numeric"
                maxLength={1}
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                style={{ width: 50 }}
                onChange={(value) => handleChange(value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                inputClassName="text-center"
                value={val}
                data-bw={`verification-code-input-${i}`}
              />
            ))}
          </section>
        </div>

        <div className="d-flex mt-4 gap-4">
          <Button
            loading={loading}
            disabled={!allFilled}
            onClick={verify}
            data-bw="verify-code"
          >
            Verify
          </Button>
        </div>
      </Card>
    </Container>
  );
};
export default TNVerification;
